// App entry point: wires the store to the views and owns the one render() call.
//
// There is no framework and no virtual DOM. Anything that changes state calls render(),
// which re-renders the active tab only. The app is small enough that this is fast and
// much easier to reason about than incremental updates.

import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/components.css";

import { PEOPLE, P } from "./data/people.js";
import { STATUS_ORDER } from "./data/classes.js";
import { byId, el, esc, readLocal, writeLocal } from "./lib/dom.js";
import { slotKey } from "./lib/schedule.js";
import { loadMe, S, setMe } from "./lib/state.js";
import * as store from "./lib/store.js";

import * as homeView from "./views/home.js";
import * as workView from "./views/work.js";
import * as loadView from "./views/load.js";
import * as availView from "./views/availability.js";
import * as readView from "./views/readings.js";

const TABS = [
  ["home", "Home"],
  ["all", "All work"],
  ["load", "Load"],
  ["avail", "Availability"],
  ["read", "Readings"],
];

// ── persistence helpers ──────────────────────────────────────────────────────

const saveItem = (it) => store.set("items", it.id, it);
const deleteItem = (id) => store.remove("items", id);
const saveAvail = () => store.set("availability", S.me, store.get("availability")[S.me] || { slots: [] });
const saveSched = () => store.set("schedule", S.me, store.get("schedule")[S.me] || { blocks: [], ex: {} });
const saveReads = () => store.set("readingChecks", S.me, store.get("readingChecks")[S.me] || { done: [] });
const saveClassOwner = (clsId, personId) => store.set("classOwners", clsId, { owner: personId });

// ── render ───────────────────────────────────────────────────────────────────

export function render() {
  TABS.forEach(([key]) => {
    document.querySelector(`[data-tab="${key}"]`).setAttribute("aria-current", key === S.tab ? "true" : "false");
    byId("v-" + key).hidden = key !== S.tab;
  });

  const items = store.get("items");
  if (S.tab === "home") homeView.render(items, store.get("classOwners"), { onSetOwner: saveClassOwner });
  else if (S.tab === "all") workView.render(items, { onSave: saveItem, onDelete: deleteItem, rerender: render });
  else if (S.tab === "load") loadView.render(items, render);
  else if (S.tab === "avail") availView.render(store.get("availability"), store.get("schedule"));
  else if (S.tab === "read") readView.render(store.get("readingChecks"), render);
}

// ── chrome ───────────────────────────────────────────────────────────────────

function buildChrome() {
  const tabHost = byId("tabs");
  TABS.forEach(([key, label]) => {
    const b = el("button", null, label);
    b.setAttribute("data-tab", key);
    b.onclick = () => {
      S.tab = key;
      render();
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    tabHost.appendChild(b);
  });

  const picker = byId("mePick");
  PEOPLE.forEach((p) => {
    const o = el("option", null, esc(p.name));
    o.value = p.id;
    picker.appendChild(o);
  });
  picker.value = loadMe();
  picker.onchange = () => {
    setMe(picker.value);
    render();
  };

  byId("themeBtn").onclick = () => {
    const root = document.documentElement;
    const current = root.getAttribute("data-theme");
    const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const next = current ? (current === "dark" ? "light" : "dark") : systemDark ? "light" : "dark";
    root.setAttribute("data-theme", next);
    writeLocal("ioh.theme", next);
  };

  const savedTheme = readLocal("ioh.theme", null);
  if (savedTheme) document.documentElement.setAttribute("data-theme", savedTheme);

  byId("addBtn").onclick = () => {
    S.adding = !S.adding;
    S.editing = null;
    render();
  };

  byId("availMode").onclick = () => {
    S.availOverlap = !S.availOverlap;
    render();
  };

  byId("addClassBtn").onclick = () => {
    S.addingClass = !S.addingClass;
    availView.renderAddClass(addBlockToMySchedule, render);
  };

  byId("availClear").onclick = () => {
    if (!confirm(`Clear all of ${P[S.me].short}’s free blocks? Classes stay put.`)) return;
    store.get("availability")[S.me] = { slots: [] };
    saveAvail();
    render();
  };

  byId("weekReset").onclick = () => {
    if (S.weekKey === "base") return;
    if (!confirm("Put every class back to its usual time for this week?")) return;
    const doc = store.get("schedule")[S.me];
    if (doc && doc.ex) delete doc.ex[S.weekKey];
    saveSched();
    render();
  };
}

function addBlockToMySchedule(block) {
  const sched = store.get("schedule");
  const doc = sched[S.me] || { blocks: [], ex: {} };
  doc.blocks = doc.blocks || [];
  doc.blocks.push(block);
  sched[S.me] = doc;
  saveSched();
}

// ── delegated events ─────────────────────────────────────────────────────────

function wireEvents() {
  document.addEventListener("click", (e) => {
    const statusBtn = e.target.closest("[data-act='status']");
    if (statusBtn) {
      const it = store.get("items")[statusBtn.getAttribute("data-id")];
      if (it) {
        it.status = STATUS_ORDER[(STATUS_ORDER.indexOf(it.status) + 1) % STATUS_ORDER.length];
        saveItem(it);
        render();
      }
      return;
    }

    const editBtn = e.target.closest("[data-act='edit']");
    if (editBtn) {
      const id = editBtn.getAttribute("data-id");
      S.editing = S.editing === id ? null : id;
      if (S.tab !== "all") S.tab = "all";
      render();
      return;
    }

    const delBlk = e.target.closest("[data-delblk]");
    if (delBlk) {
      e.stopPropagation();
      const sched = store.get("schedule");
      const updated = availView.removeBlock(sched[S.me], delBlk.getAttribute("data-delblk"), S.weekKey);
      if (updated) {
        sched[S.me] = updated;
        saveSched();
        render();
      }
      return;
    }

    const slot = e.target.closest("[data-slot2]");
    if (slot) {
      const [day, s] = slot.getAttribute("data-slot2").split("|");
      const avail = store.get("availability");
      const doc = avail[S.me] || { slots: [] };
      const key = slotKey(day, parseInt(s, 10));
      const i = doc.slots.indexOf(key);
      i > -1 ? doc.slots.splice(i, 1) : doc.slots.push(key);
      avail[S.me] = doc;
      saveAvail();
      render();
      return;
    }

    const readBtn = e.target.closest("[data-read]");
    if (readBtn) {
      const reads = store.get("readingChecks");
      const doc = reads[S.me] || { done: [] };
      const id = readBtn.getAttribute("data-read");
      const i = doc.done.indexOf(id);
      i > -1 ? doc.done.splice(i, 1) : doc.done.push(id);
      reads[S.me] = doc;
      saveReads();
      render();
    }
  });

  // Drag a class block onto a different half-hour slot.
  // Note: HTML5 drag does not fire on touch devices. Moving classes is desktop-only.
  const grid = byId("wkGrid");
  const clearDrop = () => grid.querySelectorAll(".dragover").forEach((n) => n.classList.remove("dragover"));

  grid.addEventListener("dragstart", (e) => {
    const b = e.target.closest("[data-blk]");
    if (!b) return;
    S.dragBlock = b.getAttribute("data-blk");
    b.classList.add("dragging");
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      try {
        e.dataTransfer.setData("text/plain", S.dragBlock);
      } catch {
        /* Safari can refuse setData in some contexts; the drag still works */
      }
    }
  });

  grid.addEventListener("dragend", () => {
    S.dragBlock = null;
    clearDrop();
    grid.querySelector(".dragging")?.classList.remove("dragging");
  });

  grid.addEventListener("dragover", (e) => {
    if (!S.dragBlock) return;
    const cell = e.target.closest("[data-drop]");
    if (!cell) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    clearDrop();
    cell.classList.add("dragover");
  });

  grid.addEventListener("drop", (e) => {
    if (!S.dragBlock) return;
    const cell = e.target.closest("[data-drop]");
    if (!cell) return;
    e.preventDefault();
    const [day, s] = cell.getAttribute("data-drop").split("|");
    const bid = S.dragBlock;
    S.dragBlock = null;
    clearDrop();

    const sched = store.get("schedule");
    sched[S.me] = availView.moveBlock(sched[S.me], bid, day, parseInt(s, 10), S.weekKey);
    saveSched();
    render();
  });
}

// ── boot ─────────────────────────────────────────────────────────────────────

function setConnection() {
  const { online, shared, message } = store.status();
  const node = byId("connState");
  node.className = "conn" + (online ? "" : " off");
  node.querySelector("span").textContent = message;

  if (!shared) {
    byId("windowBanners").prepend(
      el(
        "div",
        "banner warnb",
        "<div><b>Running locally.</b> The reading list and schedule work, but nothing you change here is shared " +
          "with the team — it is saved in this browser only. The shared copy is the published dashboard.</div>"
      )
    );
  }
}

buildChrome();
wireEvents();
render();

store.init().then(() => {
  store.COLLECTIONS.forEach((c) => store.on(c, () => render()));
  setConnection();
});
